import { memo, useCallback, useEffect, useState } from "react";
import { Grid, FormControlLabel, Typography, TextField, FormControl, InputLabel, Select, MenuItem, Switch, SvgIcon } from "@mui/material";
import { Box } from "@mui/system";
import { useIntl } from "react-intl";

import { IOZoneValue, ZoneBehaviors } from "./types";
import TimeTextField, { parseValue as parseTimeValue } from "../TimeTextField";
import { hexInputCheck } from "../utils";
import useEditedStyles from "../../../../hooks/useEditedStyles";
import ZoneEvent from "../zoneevent/ZoneEvent";
import { behaviors, triggers } from "./SmartalarmBoard";

export type ArrayElement<ArrayType extends readonly unknown[]> = ArrayType extends readonly (infer ElementType)[] ? ElementType : never;

interface IOZoneProps {
  index: number;
  type: string;
  value: IOZoneValue;
  saved: IOZoneValue | null;
  onChange: (index: number, value: IOZoneValue) => void;
  disabled?: boolean;
}

const IOZone = memo(({ index, type, value, saved, disabled, onChange }: IOZoneProps) => {
  const [enabled, setEnabled] = useState<boolean>(value?.enabled || true);
  const [name, setName] = useState(value?.name || "");
  const [time, setTime] = useState(value?.time?.toString(10) || "");
  const [delay, setDelay] = useState(value?.delay?.toString(10) || "");
  const [events, setEvents] = useState({ alarm: value?.eventAlarm || "", recovery: value?.eventRecovery || "" });
  const [zone, setZone] = useState(value?.zone || "");
  const [behavior, setBehavior] = useState(value?.behavior ? value.behavior.toString(2) : ZoneBehaviors.IMMEDIATE.toString(2));
  const [entryDelay, setEntryDelay] = useState(value?.entryDelay?.toString(10) || "");
  const [trigger, setTrigger] = useState<"arm" | "disarm" | "alarm" | "manual" | "">(value?.trigger || "");

  const { formatMessage } = useIntl();
  const editedStyle = useEditedStyles();

  const handleEnabled = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!value) {
        return;
      }
      setEnabled(event.target.checked);
      value.enabled = event.target.checked;
      onChange(index, value);
    },
    [value, index, onChange]
  );

  const handleTriggerChange = useCallback(
    (trigger: string) => {
      if (!value || (trigger !== "arm" && trigger !== "disarm" && trigger !== "alarm" && trigger !== "manual")) {
        return;
      }
      setTrigger(trigger);
      value.trigger = trigger;

      onChange(index, value);
    },
    [value, index, onChange]
  );

  const handleBehaviorChange = useCallback(
    (behavior: string) => {
      if (!value) {
        return;
      }
      setBehavior(behavior);
      switch (parseInt(behavior, 2)) {
        case ZoneBehaviors.DELAYED:
          value.behavior = ZoneBehaviors.DELAYED;
          break;
        case ZoneBehaviors.FOLLOWER:
          value.behavior = ZoneBehaviors.FOLLOWER;
          break;
        case ZoneBehaviors.IMMEDIATE:
          value.behavior = ZoneBehaviors.IMMEDIATE;
          break;
        case ZoneBehaviors.H24:
          value.behavior = ZoneBehaviors.H24;
          break;
      }

      onChange(index, value);
    },
    [value, index, onChange]
  );

  useEffect(() => {
    setEnabled(value ? value.enabled : true);
    setName(value?.name || "");
    setTime(value?.time?.toString(10) || "");
    setDelay(value?.delay?.toString(10) || "");
    setEvents({ alarm: value?.eventAlarm || "", recovery: value?.eventRecovery || "" });
    setZone(value?.zone || "");
    setBehavior(value?.behavior ? value.behavior.toString(2) : "");
    setEntryDelay(value?.entryDelay?.toString(10) || "");
    setTrigger(value?.trigger || "");
  }, [value]);

  const onZoneEventChange = useCallback(
    ({ alarm, recovery }) => {
      setEvents({ alarm, recovery: recovery || "" });
      value.eventAlarm = alarm;
      value.eventRecovery = recovery || null;
      onChange(index, value);
    },
    [index, onChange, value]
  );

  if (type === "NULL") {
    return null;
  }

  return (
    <Box component="form" noValidate autoComplete="off" sx={[{ display: "flex", flexWrap: "wrap", mt: 1, mb: 2 }]}>
      <Box sx={{ display: "flex" }}>
        <Grid container spacing={1} alignItems="center" sx={[{ width: "unset" }]} className={enabled ? "" : " disabledrow"}>
          {type !== "OUT" && (
            <Grid item xs={12} sm={6} md={4} lg={3} xl="auto">
              <FormControl
                variant="filled"
                size="small"
                sx={{ margin: 0 }}
                className={saved?.behavior?.toString(2) !== behavior ? editedStyle.input : ""}
                disabled={disabled}
                fullWidth
              >
                <InputLabel id="behavior-select-label">{formatMessage({ defaultMessage: "Behavior" })}</InputLabel>
                <Select labelId="behavior-select-label" value={behavior} onChange={(e) => handleBehaviorChange(e.target.value)} color="secondary">
                  {behaviors?.map((behavior) => (
                    <MenuItem key={`behavior-${behavior.value}`} value={behavior.value}>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <SvgIcon sx={{ color: behavior.color, mr: 1 }}>{behavior?.icon}</SvgIcon>
                        {behavior.label ?? ""}
                      </div>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          {type === "OUT" && (
            <Grid item xs={12} sm={6} md={4} lg={3} xl="auto">
              <FormControl
                variant="filled"
                size="small"
                color="secondary"
                sx={{ margin: 0 }}
                className={saved?.trigger !== trigger ? editedStyle.input : ""}
                disabled={disabled}
                fullWidth
              >
                <InputLabel id="select-label">{formatMessage({ defaultMessage: "Trigger" })}</InputLabel>
                <Select labelId="select-label" value={trigger} onChange={(e) => handleTriggerChange(e.target.value)}>
                  {triggers.map((option) => (
                    <MenuItem key={`option-${option.value}`} value={option.value}>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <SvgIcon sx={{ color: option.color, mr: 1 }}>{option?.icon}</SvgIcon>
                        {option.label}
                      </div>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={4} lg={3} xl="auto">
            <TextField
              label={formatMessage({ defaultMessage: "Zone" })}
              variant="filled"
              value={zone}
              className={saved?.zone !== zone ? editedStyle.input : ""}
              inputProps={{ maxLength: 3, style: { textTransform: "uppercase" } }}
              onChange={(e) => hexInputCheck(e.target.value, setZone)}
              onBlur={() => {
                const v = zone ? zone.padStart(3, "0") : zone;
                value.zone = v;
                // setZone(v);
                onChange(index, value);
              }}
              sx={{ margin: 0, minWidth: { xl: 80 }, width: { xl: 80 } }}
              size="small"
              color="secondary"
              disabled={disabled}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3} xl="auto">
            <TextField
              label={formatMessage({ defaultMessage: "Name" })}
              variant="filled"
              value={name}
              className={saved?.name !== name && !(value.name === null && name === "") ? editedStyle.input : ""}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => {
                value.name = name;
                onChange(index, value);
              }}
              size="small"
              color="secondary"
              sx={{ margin: 0, minWidth: { xl: 130 }, width: { xl: 130 } }}
              disabled={disabled}
              fullWidth
            />
          </Grid>
          <ZoneEvent
            value={events}
            saved={saved ? { alarm: saved.eventAlarm, recovery: saved.eventRecovery } : undefined}
            onChange={onZoneEventChange}
            emptyRecovery={null}
            color="secondary"
            disabled={disabled}
          />
          {type !== "OUT" && behavior === ZoneBehaviors.DELAYED.toString(2) && (
            <Grid item xs={12} sm={6} md={4} lg={3} xl="auto">
              <TimeTextField
                label={formatMessage({ defaultMessage: "Entry delay" })}
                variant="filled"
                value={entryDelay}
                className={saved?.entryDelay !== parseInt(entryDelay, 10) ? editedStyle.input : ""}
                onChange={(e) => setEntryDelay(`${e.target.value}`)}
                onBlur={() => {
                  value.entryDelay = parseTimeValue(entryDelay);
                  onChange(index, value);
                }}
                sx={{ margin: 0, minWidth: { xl: 220 }, width: { xl: 220 } }}
                size="small"
                color="secondary"
                disabled={disabled}
                fullWidth
              />
            </Grid>
          )}
          {type === "OUT" && (
            <>
              <Grid item xs={12} sm={6} md={4} lg={3} xl="auto">
                <TimeTextField
                  label={formatMessage({ defaultMessage: "Impulse time" })}
                  zeroLabel={formatMessage({ defaultMessage: "continuous" })}
                  variant="filled"
                  value={time}
                  className={saved?.time !== parseInt(time, 10) && !(!saved?.time && !time) ? editedStyle.input : ""}
                  onChange={(e) => setTime(`${e.target.value}`)}
                  onBlur={() => {
                    if (time !== "") {
                      value.time = parseTimeValue(time);
                    } else {
                      delete value.time;
                    }
                    onChange(index, value);
                  }}
                  sx={{ margin: 0, minWidth: { xl: 160 }, width: { xl: 160 } }}
                  size="small"
                  color="secondary"
                  disabled={disabled}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3} xl="auto">
                <TimeTextField
                  label={formatMessage({ defaultMessage: "Delay" })}
                  variant="filled"
                  value={delay}
                  className={saved?.delay !== parseInt(delay, 10) && !(!saved?.delay && !delay) ? editedStyle.input : ""}
                  onChange={(e) => setDelay(`${e.target.value}`)}
                  onBlur={() => {
                    if (delay !== "") {
                      value.delay = parseTimeValue(delay);
                    } else {
                      delete value.delay;
                    }
                    onChange(index, value);
                  }}
                  sx={{ margin: 0, minWidth: { xl: 160 }, width: { xl: 160 } }}
                  size="small"
                  color="secondary"
                  disabled={disabled}
                  fullWidth
                />
              </Grid>
            </>
          )}
        </Grid>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "flex-end", flex: 1 }}>
        <FormControlLabel
          control={<Switch checked={enabled} color="secondary" onChange={handleEnabled} />}
          label={<Typography variant="caption">{formatMessage({ defaultMessage: "Enabled" })}</Typography>}
          labelPlacement="end"
          className={saved?.enabled !== enabled ? editedStyle.background : ""}
          disabled={disabled}
          sx={{}}
        />
      </Box>
    </Box>
  );
});

export default IOZone;
